import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Palette, Upload, Trash2, Loader2, Sparkles, ShieldCheck, Save } from "lucide-react";
import Layout from "@/components/Layout";
import api, { API } from "@/lib/api";
import { toast } from "sonner";

const ORIGIN = API.replace(/\/api\/?$/, "");

export default function Branding() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [isAgency, setIsAgency] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [brandName, setBrandName] = useState("");
  const [accent, setAccent] = useState("#007AFF");
  const [footer, setFooter] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoBust, setLogoBust] = useState(Date.now());

  const load = async () => {
    try {
      const res = await api.get("/branding");
      setIsAgency(Boolean(res.data.is_agency));
      const b = res.data.branding || {};
      setBrandName(b.brand_name || "");
      setAccent(b.accent_color || "#007AFF");
      setFooter(b.footer_note || "");
      setEnabled(b.enabled !== false);
      setLogoUrl(b.logo_url || null);
    } catch {
      /* noop */
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/branding", {
        brand_name: brandName, accent_color: accent, footer_note: footer, enabled,
      });
      toast.success("Branding saved — it now appears on your downloadable & shared reports.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save branding.");
    } finally { setSaving(false); }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/branding/logo", fd);
      setLogoUrl(res.data.logo_url);
      setLogoBust(Date.now());
      setEnabled(true);
      toast.success("Logo uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Logo upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    try {
      await api.delete("/branding/logo");
      setLogoUrl(null);
      toast.success("Logo removed.");
    } catch {
      toast.error("Could not remove the logo.");
    }
  };

  if (loading) {
    return <Layout><div className="flex items-center justify-center py-32"><Loader2 className="animate-spin text-zinc-500" /></div></Layout>;
  }

  const logoSrc = logoUrl ? `${ORIGIN}${logoUrl}?_t=${logoBust}` : null;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="branding-page">
        <div className="flex items-center gap-2 mb-1">
          <Palette className="text-[#9b5cff]" size={20} />
          <h1 className="font-heading text-2xl font-bold text-white">White-Label Reports</h1>
        </div>
        <p className="text-zinc-500 text-sm mb-8">
          Put your own logo, name and colors on the reports you download and share with clients.
        </p>

        {!isAgency && (
          <div className="mb-8 rounded-xl border border-[#9b5cff]/30 bg-[#9b5cff]/5 p-5" data-testid="branding-upgrade-gate">
            <div className="flex items-center gap-2 text-white font-semibold mb-1">
              <Sparkles size={16} className="text-[#9b5cff]" /> An Agency feature
            </div>
            <p className="text-zinc-400 text-sm mb-3">
              White-label branding is included with the Agency plan. Upgrade to remove Link Website Pro
              branding and ship reports under your own brand.
            </p>
            <button data-testid="branding-upgrade-btn" onClick={() => navigate("/pricing")}
              className="btn-brand text-white rounded-lg px-4 py-2 text-sm font-medium">
              See Agency plan
            </button>
          </div>
        )}

        <div className={`grid md:grid-cols-2 gap-6 ${!isAgency ? "opacity-60 pointer-events-none select-none" : ""}`}>
          {/* Controls */}
          <div className="space-y-5">
            <div className="bg-[#121212] border border-white/10 rounded-xl p-5">
              <label className="block text-xs uppercase tracking-wide text-zinc-400 mb-2">Logo</label>
              <div className="flex items-center gap-3">
                <div className="h-16 w-16 rounded-lg bg-black/40 border border-white/10 flex items-center justify-center overflow-hidden shrink-0">
                  {logoSrc
                    ? <img src={logoSrc} alt="logo" className="max-h-14 max-w-14 object-contain" data-testid="branding-logo-thumb" />
                    : <span className="text-zinc-600 text-xs">No logo</span>}
                </div>
                <div className="flex flex-col gap-2">
                  <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp,.gif,.svg" onChange={onPickFile} className="hidden" data-testid="branding-logo-input" />
                  <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="branding-logo-upload-btn"
                    className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white hover:bg-white/10 transition-colors disabled:opacity-60">
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    {uploading ? "Uploading…" : "Upload logo"}
                  </button>
                  {logoUrl && (
                    <button onClick={removeLogo} data-testid="branding-logo-remove-btn"
                      className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-[#FF3B30] transition-colors">
                      <Trash2 size={12} /> Remove
                    </button>
                  )}
                </div>
              </div>
              <p className="text-[11px] text-zinc-600 mt-2">PNG, JPG, WEBP, GIF or SVG · up to 2 MB.</p>
            </div>

            <div className="bg-[#121212] border border-white/10 rounded-xl p-5 space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wide text-zinc-400 mb-1.5">Brand name</label>
                <input value={brandName} onChange={(e) => setBrandName(e.target.value)} maxLength={60}
                  placeholder="Your agency name" data-testid="branding-name-input"
                  className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#9b5cff]/60" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wide text-zinc-400 mb-1.5">Accent color</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} data-testid="branding-accent-input"
                    className="h-9 w-12 rounded bg-transparent border border-white/15 cursor-pointer" />
                  <input value={accent} onChange={(e) => setAccent(e.target.value)} maxLength={20}
                    className="flex-1 bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm font-mono text-white focus:outline-none focus:border-[#9b5cff]/60" />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wide text-zinc-400 mb-1.5">Report footer note</label>
                <input value={footer} onChange={(e) => setFooter(e.target.value)} maxLength={200}
                  placeholder="e.g. Prepared by Acme Digital · hello@acme.com" data-testid="branding-footer-input"
                  className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#9b5cff]/60" />
              </div>
              <label className="flex items-center gap-2.5 cursor-pointer" data-testid="branding-enabled-toggle">
                <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="accent-[#9b5cff]" />
                <span className="text-sm text-zinc-300">Apply my branding to reports</span>
              </label>
            </div>

            <button onClick={save} disabled={saving} data-testid="branding-save-btn"
              className="w-full inline-flex items-center justify-center gap-2 btn-brand text-white rounded-lg px-4 py-2.5 text-sm font-semibold disabled:opacity-60">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
              {saving ? "Saving…" : "Save branding"}
            </button>
          </div>

          {/* Live preview */}
          <div>
            <div className="text-xs uppercase tracking-wide text-zinc-400 mb-2">Report header preview</div>
            <div className="bg-white rounded-xl overflow-hidden border border-white/10" data-testid="branding-preview">
              <div className="p-6" style={{ borderBottom: `3px solid ${enabled ? accent : "#4f46e5"}` }}>
                {enabled && (brandName || logoSrc) ? (
                  <div className="flex items-center gap-3 mb-3">
                    {logoSrc && <img src={logoSrc} alt="logo" className="max-h-11 max-w-[180px] object-contain" />}
                    {brandName && <div className="font-extrabold text-lg" style={{ color: accent }}>{brandName}</div>}
                  </div>
                ) : (
                  <div className="text-[13px] text-zinc-500 mb-2">Link Website Pro</div>
                )}
                <div className="text-[#1a1a1a] text-xl font-bold">Website Security &amp; Technology Report</div>
                <div className="text-zinc-500 text-xs mt-1">example.com · Generated just now</div>
              </div>
              <div className="p-6 space-y-2">
                <div className="h-3 w-2/3 bg-zinc-200 rounded" />
                <div className="h-3 w-1/2 bg-zinc-200 rounded" />
                <div className="h-3 w-3/4 bg-zinc-100 rounded" />
                <div className="mt-4 flex items-center gap-2 text-[#065f46] text-xs">
                  <ShieldCheck size={14} /> This is how clients will see your report.
                </div>
              </div>
              <div className="px-6 py-3 border-t border-zinc-100 text-[11px] text-zinc-400">
                {enabled && footer ? `${footer} · ` : ""}Generated just now.
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
